import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatCommonModule } from "@angular/material/core";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatRadioModule } from "@angular/material/radio";
import { MatSelectChange, MatSelectModule } from "@angular/material/select";
import { MatListModule, MatSelectionListChange } from "@angular/material/list";
import { combineLatest, ReplaySubject } from "rxjs";
import { debounceTime, firstValueFrom, map, of, startWith, switchMap, take } from "rxjs";
import { MatChipsModule } from "@angular/material/chips";
import { MatIconModule } from "@angular/material/icon";
import { CompanyInformation } from "src/app/iap/models/myProfile.model";
import { MyProfileService } from "src/app/iap/services/my-profile.service";
import { LookupsService } from "src/app/shared/services/lookups.service";
import { environment } from "src/environments/environment";
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { CustomToasterComponent } from 'src/app/shared/components/custom-toaster/custom-toaster.component';

@Component({
    selector: "app-onboarding-company-information",
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatSelectModule,
        MatListModule,
        MatChipsModule,
        MatIconModule,
        MatCommonModule,
        MatRadioModule,
        MatSnackBarModule
    ],
    templateUrl: "./onboarding-company-information.component.html",
    styleUrl: "./onboarding-company-information.component.scss"
})
export class OnboardingCompanyInformationComponent implements OnInit {
  //Holds info from the DB
  @Input() CompanyInfo: CompanyInformation;
  @Input() agencyId: string; //agency id
  @Input() agentId: string; //agent id
  @Input() businessInfoStatus: string; //business info status
  @Output() saveCompanyInfo = new EventEmitter<{
    companyInformationFormData;
  }>();

  constructor(
    private _formBuilder: FormBuilder,
    private lookupService: LookupsService,
    private myProfileService: MyProfileService,
    private snackBar: MatSnackBar
  ) {}

  countryList: any = [];
  businessTypeList = ["Corporation", "Partnership", "Sole Proprietorship"];
  showFillTestDataButton: boolean = false; //show button only in dev mode

  companyInformationForm: FormGroup = this._formBuilder.group({
    businessType: ["", Validators.required],
    companyName: ["", [Validators.required, Validators.pattern(/(.|\s)*\S(.|\s)*/)]],
    registrationNumber: ["", [Validators.required, Validators.pattern(/(.|\s)*\S(.|\s)*/)]],
    companyWebsite: [""],                          // optional
    registeredCanadianBusiness: ["", Validators.required],
    // minLength works on arrays; Validators.required treats [] as valid
    registeredCountries: [[], [Validators.required, Validators.minLength(1)]],
    registeredStudentCountries: [[], [Validators.required, Validators.minLength(1)]],
    contactTitle: ["", [Validators.required, Validators.pattern(/(.|\s)*\S(.|\s)*/)]],
    contactName: ["", [Validators.required, Validators.pattern(/^[A-Za-z]+(?:[ '-][A-Za-z]+)*$/)]],
    email: ["", [Validators.required, Validators.email]],
    phoneCountryCode: ["", [Validators.required, Validators.pattern(/^\+?[0-9]{1,3}$/)]],
    phone: ["", [Validators.required, Validators.minLength(7), Validators.pattern(/^[0-9]{7,12}$/)]],
    phoneOld: [""],
    address1: ["", [Validators.required, Validators.pattern(/(.|\s)*\S(.|\s)*/)]],
    address2: [""],                                // optional
    postalCode: [""],                              // optional
    city: ["", [Validators.required, Validators.pattern(/(.|\s)*\S(.|\s)*/)]],
    country: ["", Validators.required],
    state: [""],                                   // optional
    agentID: [""], //hidden field
    legalName: ["", [Validators.required, Validators.pattern(/(.|\s)*\S(.|\s)*/)]],
  });

  searchCountries = new FormControl();
  searchStudentCountries = new FormControl();
  isLoadingCountries = true;

  $searchCountries = this.searchCountries.valueChanges.pipe(
    startWith(null),
    debounceTime(200),
    switchMap((res: string) => {
      if (!res) return of(this.countryList);
      res = res.toLowerCase();
      return of(
        this.countryList.filter((x: { Name: string }) => {
          let name: string = x.Name;
          return name.toLowerCase().indexOf(res) >= 0;
        })
      );
    })
  );

  $searchStudentCountries = this.searchStudentCountries.valueChanges.pipe(
    startWith(null),
    debounceTime(200),
    switchMap((res: string) => {
      if (!res) return of(this.countryList);
      res = res.toLowerCase();
      return of(
        this.countryList.filter((x: { Name: string }) => {
          let name: string = x.Name;
          return name.toLowerCase().indexOf(res) >= 0;
        })
      );
    })
  );

  private parseCountriesToArray(val: any): string[] {
    if (!val) {
      return [];
    }
    // If it's a FormControl (due to patchValue nesting), recursively extract value
    if (val && typeof val === "object" && "value" in val && typeof val.setValue === "function") {
      return this.parseCountriesToArray(val.value);
    }
    // If it's a string, split it
    if (typeof val === "string") {
      return val.split(",").map((s) => s.trim());
    }
    // If it's an array
    if (Array.isArray(val)) {
      if (val.length > 0 && typeof val[0] === "object" && val[0] !== null) {
        return val.map((item: any) => item.Country_Code || item.Code || "").filter(Boolean);
      }
      return val.map((item) => (typeof item === "string" ? item : item?.Country_Code || item?.Code || "")).filter(Boolean);
    }
    return [];
  }

  isCountrySelected(code: string): boolean {
    const selectedValue = this.companyInformationForm.get("registeredCountries")?.value;
    const selected = this.parseCountriesToArray(selectedValue);
    return selected.includes(code);
  }

  isStudentCountrySelected(code: string): boolean {
    const selectedValue = this.companyInformationForm.get("registeredStudentCountries")?.value;
    const selected = this.parseCountriesToArray(selectedValue);
    return selected.includes(code);
  }

  getSelectedCountries(): any[] {
    const selectedCodes = this.parseCountriesToArray(this.companyInformationForm.get("registeredCountries")?.value);
    return (this.countryList || []).filter((c: any) => selectedCodes.includes(c.Code));
  }

  getSelectedStudentCountries(): any[] {
    const selectedCodes = this.parseCountriesToArray(this.companyInformationForm.get("registeredStudentCountries")?.value);
    return (this.countryList || []).filter((c: any) => selectedCodes.includes(c.Code));
  }

  removeCountry(code: string) {
    if (this.companyInformationForm.get("registeredCountries").disabled) {
      return;
    }
    const currentSelection = this.parseCountriesToArray(this.companyInformationForm.get("registeredCountries")?.value);
    const newSelection = currentSelection.filter((c) => c !== code);
    this.companyInformationForm.get("registeredCountries")?.setValue(newSelection);
    this.companyInformationForm.get("registeredCountries")?.markAsTouched();
  }

  removeStudentCountry(code: string) {
    if (this.companyInformationForm.get("registeredStudentCountries").disabled) {
      return;
    }
    const currentSelection = this.parseCountriesToArray(this.companyInformationForm.get("registeredStudentCountries")?.value);
    const newSelection = currentSelection.filter((c) => c !== code);
    this.companyInformationForm.get("registeredStudentCountries")?.setValue(newSelection);
    this.companyInformationForm.get("registeredStudentCountries")?.markAsTouched();
  }

  //  agentId: number;
  lastModified: string;
  user: any;
  emailString: string;
  isSaved: boolean = false;

  ngOnInit() {
    this.loadCountries();
    this.companyInformationForm.patchValue({
      agentID: this.agentId,
    });
    //console.log(this.authService.getUserInfo());

    //Temp Agent Id for testing
    //  this.user = this.authService.getUserInfo();
    console.log(
      "Agency ID in the company information component: ",
      this.agencyId
    );
    // await this.getAgencyInformation();
    this.createForm();
    console.log("User: ", this.user?.oid);
    this.showFillTestDataButton = environment.showTestButton;

    // this.companyInformationForm.valueChanges.subscribe(data => {
    //   if (this.companyInformationForm.valid) {
    //     console.log("Data: ", data);
    //     this.emitCompanyInfo();
    //   }
    // });

    //  this.myProfileService
    //    .getAgencyInformation(this.user.oid)
    //    .subscribe(agencyInfo => {
    //     console.log("Agency Info: ", agencyInfo);
    //      this.agentId = agencyInfo.agencyId;
    //      //this.CompanyInfo = this.myProfileService.getAgentProfileById(this.agentId);
    //     //  this.myProfileService.getAllCountryList().subscribe(results => {
    //     //    this.countryList = results;
    //     //    this.getCompanyInfo();
    //     //  });
    //    });
  }

  onSelectionChange(event: MatSelectionListChange) {
    if (this.companyInformationForm.get("registeredCountries").disabled) {
      return;
    }

    const currentSelection = this.parseCountriesToArray(this.companyInformationForm.get("registeredCountries").value);
    let newSelection = [...currentSelection];

    event.options.forEach((option) => {
      if (option.selected) {
        if (!newSelection.includes(option.value)) {
          newSelection.push(option.value);
        }
      } else {
        newSelection = newSelection.filter((val) => val !== option.value);
      }
    });

    if (newSelection.length > 3) {
      // Revert the option selection in the UI
      event.options.forEach((option) => {
        if (option.selected) {
          option.selected = false;
        }
      });
      this.snackBar.openFromComponent(CustomToasterComponent, {
        data: { title: 'ERROR', message: 'You can only select up to 3 countries of recruitment.', type: 'error' },
        duration: 3000,
        horizontalPosition: 'right',
        verticalPosition: 'top',
        panelClass: ['custom-toast-panel']
      });
      return;
    }

    this.companyInformationForm.get("registeredCountries").setValue(newSelection);
    this.companyInformationForm.get("registeredCountries").markAsTouched();
  }

  onSelectionChangeStudent(event: MatSelectionListChange) {
    if (this.companyInformationForm.get("registeredStudentCountries").disabled) {
      return;
    }

    const currentSelection = this.parseCountriesToArray(this.companyInformationForm.get("registeredStudentCountries").value);
    let newSelection = [...currentSelection];

    event.options.forEach((option) => {
      if (option.selected) {
        if (!newSelection.includes(option.value)) {
          newSelection.push(option.value);
        }
      } else {
        newSelection = newSelection.filter((val) => val !== option.value);
      }
    });

    if (newSelection.length > 3) {
      // Revert the option selection in the UI
      event.options.forEach((option) => {
        if (option.selected) {
          option.selected = false;
        }
      });
      this.snackBar.openFromComponent(CustomToasterComponent, {
        data: { title: 'ERROR', message: 'You can only select up to 3 student nationalities.', type: 'error' },
        duration: 3000,
        horizontalPosition: 'right',
        verticalPosition: 'top',
        panelClass: ['custom-toast-panel']
      });
      return;
    }

    this.companyInformationForm.get("registeredStudentCountries").setValue(newSelection);
    this.companyInformationForm.get("registeredStudentCountries").markAsTouched();
  }

  onEnterCountries(event: Event) {
    event.preventDefault();

    const filterText = (this.searchCountries.value || "").trim().toLowerCase();
    if (!filterText) {
      return;
    }

    const visibleCountries = (this.countryList || []).filter((c: any) =>
      (c.Name || "").toLowerCase().includes(filterText)
    );

    if (visibleCountries.length > 0) {
      const firstCountry = visibleCountries[0];
      const currentSelection = this.parseCountriesToArray(this.companyInformationForm.get("registeredCountries")?.value);

      if (!currentSelection.includes(firstCountry.Code)) {
        if (currentSelection.length >= 3) {
          this.snackBar.openFromComponent(CustomToasterComponent, {
            data: { title: 'ERROR', message: 'You can only select up to 3 countries of recruitment.', type: 'error' },
            duration: 3000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: ['custom-toast-panel']
          });
          return;
        }

        const newSelection = [...currentSelection, firstCountry.Code];
        this.companyInformationForm.get("registeredCountries")?.setValue(newSelection);
        this.companyInformationForm.get("registeredCountries")?.markAsTouched();
      }

      // Clear search input
      this.searchCountries.setValue("");
    }
  }

  onEnterStudentCountries(event: Event) {
    event.preventDefault();

    const filterText = (this.searchStudentCountries.value || "").trim().toLowerCase();
    if (!filterText) {
      return;
    }

    const visibleCountries = (this.countryList || []).filter((c: any) =>
      (c.Name || "").toLowerCase().includes(filterText)
    );

    if (visibleCountries.length > 0) {
      const firstCountry = visibleCountries[0];
      const currentSelection = this.parseCountriesToArray(this.companyInformationForm.get("registeredStudentCountries")?.value);

      if (!currentSelection.includes(firstCountry.Code)) {
        if (currentSelection.length >= 3) {
          this.snackBar.openFromComponent(CustomToasterComponent, {
            data: { title: 'ERROR', message: 'You can only select up to 3 student nationalities.', type: 'error' },
            duration: 3000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: ['custom-toast-panel']
          });
          return;
        }

        const newSelection = [...currentSelection, firstCountry.Code];
        this.companyInformationForm.get("registeredStudentCountries")?.setValue(newSelection);
        this.companyInformationForm.get("registeredStudentCountries")?.markAsTouched();
      }

      // Clear search input
      this.searchStudentCountries.setValue("");
    }
  }

  async getAgencyInformation() {
    console.log(
      "Agency ID in the company information component: ",
      this.agencyId
    );
    try {
      const agencyInfo = await firstValueFrom(
        this.myProfileService.getAgencyInformation(this.agencyId)
      );

      console.log("Agency Info: ", agencyInfo);
      
      // Ensure registeredCountries is always an array
      let registeredCountriesArray: any[] = [];
      if (Array.isArray(agencyInfo?.registeredCountries)) {
        if (agencyInfo.registeredCountries.length > 0 && typeof agencyInfo.registeredCountries[0] === 'object') {
          registeredCountriesArray = agencyInfo.registeredCountries.map((country) => country.Country_Code);
        } else {
          registeredCountriesArray = agencyInfo.registeredCountries;
        }
      }
      this.companyInformationForm
        .get("registeredCountries")
        .setValue(registeredCountriesArray);

      // Ensure registeredStudentCountries is always an array
      let registeredStudentCountriesArray: any[] = [];
      if (Array.isArray(agencyInfo?.registeredStudentCountries)) {
        if (agencyInfo.registeredStudentCountries.length > 0 && typeof agencyInfo.registeredStudentCountries[0] === 'object') {
          registeredStudentCountriesArray = agencyInfo.registeredStudentCountries.map((country) => country.Country_Code);
        } else {
          registeredStudentCountriesArray = agencyInfo.registeredStudentCountries;
        }
      }
      this.companyInformationForm.get('registeredStudentCountries').setValue(registeredStudentCountriesArray);

      this.CompanyInfo = agencyInfo;
      console.log("Company Info from DB: ", this.CompanyInfo);
    } catch (error) {
      console.error("Error fetching agency information: ", error);
    }
  }

  //  save() {
  //   console.log("Company Information Form: ", this.companyInformationForm.value);
  //   if (this.companyInformationForm.valid) {
  //     this.myProfileService.insertAgencyInformation(this.companyInformationForm.value, this.agencyId).subscribe(result => {
  //       console.log("Result: ", result);
  //       this.isSaved = true;
  //     });
  //   }
  //  }

  save() {
    if (!this.companyInformationForm.valid) {
      this.companyInformationForm.markAllAsTouched();
      console.log("Form is NOT VALID");
      return;
    }

    console.log(
      "Company Information Form: ",
      this.companyInformationForm.value
    );
    this.saveCompanyInfo.emit(this.companyInformationForm.value);
    sessionStorage.setItem('onboarding_business_draft', JSON.stringify(this.companyInformationForm.getRawValue()));
    this.snackBar.openFromComponent(CustomToasterComponent, {
      data: { title: 'SUCCESS', message: 'Business Entity information saved successfully!', type: 'success' },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['custom-toast-panel']
    });
  }

  private createForm() {
    console.log("Company Info in create form: ", this.CompanyInfo);
    let country1 = "";
    let country2 = "";
    let country3 = "";
    let studentCountry1 = "";
    let studentCountry2 = "";
    let studentCountry3 = "";

    // if (this.CompanyInfo.registeredCountries.length > 0) {
    //   country1 = this.CompanyInfo.registeredCountries[0].Country_Code;
    //   if (this.CompanyInfo.registeredCountries.length > 1) {
    //     country2 = this.CompanyInfo.registeredCountries[1].Country_Code;
    //     if (this.CompanyInfo.registeredCountries.length > 2) {
    //       country3 = this.CompanyInfo.registeredCountries[2].Country_Code;
    //     }
    //   }
    // }
    //bind values to the form
    this.companyInformationForm.patchValue({
      businessType: this.CompanyInfo?.["businessType"] || "",
      legalName: this.CompanyInfo?.legalName || "",
      companyName: this.CompanyInfo?.cName || "",
      registrationNumber: this.CompanyInfo?.["registrationNumber"] || "",
      companyWebsite: this.CompanyInfo?.companyWebsite || "",
      registeredCanadianBusiness: this.CompanyInfo?.registeredCanadianBusiness ?? "",
      registeredCountries: this.parseCountriesToArray(this.CompanyInfo?.registeredCountries),
      registeredStudentCountries: this.parseCountriesToArray(this.CompanyInfo?.["registeredStudentCountries"]),
      contactTitle: this.CompanyInfo?.contactTitle || "",
      contactName: this.CompanyInfo?.contactName || "",
      email: this.CompanyInfo?.email || "",
      phoneCountryCode: this.CompanyInfo?.phoneCountryCode || "",
      phone: this.CompanyInfo?.phone || "",
      phoneOld: this.CompanyInfo?.phoneOld || "",
      address1: this.CompanyInfo?.address1 || "",
      address2: this.CompanyInfo?.address2 || "",
      postalCode: this.CompanyInfo?.postalCode || "",
      city: this.CompanyInfo?.city || "",
      country: this.CompanyInfo?.country || "",
      state: this.CompanyInfo?.state || "",
      // agentID: this.agentId
    });

    const draft = sessionStorage.getItem('onboarding_business_draft');
    if (draft) {
      try {
        this.companyInformationForm.patchValue(JSON.parse(draft));
      } catch (e) {
        console.error("Failed to parse onboarding business draft", e);
      }
    }
  }

  loadCountries() {
    // Use the lookup service to get Countries and Codes
    this.lookupService
      .getLookupByTableAlias("country")
      .pipe(take(1))
      .subscribe((country) => {
        this.countryList = country;
        this.isLoadingCountries = false;
      });
  }

  fillTestData(): void {
    this.companyInformationForm.patchValue({
      businessType: "Corporation",
      companyName: "ABC Education Services Inc.",
      registrationNumber: "123456789RC0001",
      companyWebsite: "www.abceducation.com",
      registeredCanadianBusiness: true,
      registeredCountries: ["CAN", "IND", "CHN"],
      registeredStudentCountries: ["CAN", "IND", "CHN"],
      contactTitle: "Director",
      contactName: "John Smith",
      email: "john.smith@abceducation.com",
      phoneCountryCode: "1",
      phone: "4165550123",
      phoneOld: "4165550124",
      address1: "123 Education Street",
      address2: "Suite 100",
      postalCode: "M5V 2T6",
      city: "Toronto",
      country: "CAN",
      state: "Ontario",
      agentID: this.agentId,
      legalName: "ABC Education Services Inc. and Co. and Partners and Associates and Sons and Daughters and Family Ltd.",
    });
  }
}
